"use client";

import { useMemo } from "react";
import { Button } from "@ui/components/core/Button";
import { Checkbox } from "@ui/components/core/Checkbox";
import { useTranslations } from "next-intl";
import { createFormHook, createFormHookContexts } from "@tanstack/react-form";
import { Input } from "@/app/_components/ContactSalesV2/components/Input";
import { PhoneInput } from "@/app/_components/ContactSalesV2/components/PhoneInput";
import { string, pipe, trim, nonEmpty, maxLength, regex, check, safeParse } from "valibot";
import { HeroAgentCardLayout, ResizableProgressBar } from "./Components";
import type { UseMutationResult } from "@ui/hooks/useMutation";
import { ArrowLeft, LoaderCircle } from "lucide-react";
import { captureEvent } from "@/app/_analytics/analytics";
import { EventName } from "@mw/shared/analyticsEventNames";

// Form types
type AgentRequestCallFormValues = {
  name: string;
  phone_number: string;
  consent: boolean;
};

// Create form context and hooks
const { fieldContext, formContext } = createFormHookContexts();

const { useAppForm } = createFormHook({
  fieldContext,
  formContext,
  fieldComponents: {
    Input: Input,
    PhoneInput: PhoneInput,
  },
  formComponents: {},
});

/** Type for the request call mutation result */
export type RequestCallMutationResult = UseMutationResult<
  { message: string; retry_after_seconds?: number | null },
  Error,
  { phone_number: string }
>;

export interface AgentRequestCallViewProps {
  onSubmit: (data: { name: string; phone: string; consent: boolean }) => void;
  onCancel: () => void;
  /** Current step index (0-based) for progress indicator */
  currentStep: number;
  /** Total number of steps for progress indicator */
  totalSteps: number;
  /** Mutation for requesting a call (sends OTP to phone) - passed from parent */
  requestCallMutation: RequestCallMutationResult;
  /** Agent ID for tracking events */
  agentId: string;
}

export function AgentRequestCallView({
  onSubmit,
  onCancel,
  currentStep,
  totalSteps,
  requestCallMutation,
  agentId,
}: AgentRequestCallViewProps) {
  const t = useTranslations("ClientKeys.hero_agent.views.request_call");
  const tValidation = useTranslations("ClientKeys.forms.validation");

  // Memoized Valibot schemas for validation
  const nameSchema = useMemo(() => pipe(string(), trim(), nonEmpty(tValidation("required"))), [tValidation]);

  const phoneSchema = useMemo(
    () =>
      pipe(
        string(),
        trim(),
        nonEmpty(tValidation("required")),
        maxLength(50, tValidation("max_length", { max: 50 })),
        regex(/^\+?[\d\s().\-]+$/, tValidation("phone")),
        check(value => {
          const digitCount = value.replace(/\D/g, "").length;
          return digitCount >= 7;
        }, tValidation("phone")),
      ),
    [tValidation],
  );

  const form = useAppForm({
    defaultValues: {
      name: "",
      phone_number: "",
      consent: false as boolean,
    } satisfies AgentRequestCallFormValues,
    onSubmit: async ({ value }) => {
      captureEvent(EventName.mw_hero_agent_request_call_submitted, { agent_id: agentId });
      try {
        await requestCallMutation.mutateAsync({ phone_number: value.phone_number });
        captureEvent(EventName.mw_hero_agent_request_call_succeeded, { agent_id: agentId });
        // Only proceed to next step if mutation succeeded
        onSubmit({
          name: value.name,
          phone: value.phone_number,
          consent: value.consent,
        });
      } catch (error) {
        captureEvent(EventName.mw_hero_agent_request_call_failed, {
          agent_id: agentId,
          error_message: error instanceof Error ? error.message : "Unknown error",
        });
        // Error is already handled by mutation's onError
      }
    },
  });

  return (
    <HeroAgentCardLayout width='full' className='tw-h-full tw-flex tw-flex-col tw-py-2.5'>
      <div className='tw-flex tw-flex-col tw-max-w-sm'>
        <h3 className='tw-mb-4 tw-type-xl sm:tw-type-2xl'>{t("title")}</h3>
        <p className='tw-text-gray-500 tw-mb-6 tw-type-2xs sm:tw-type-xs'>{t("subtitle")}</p>
      </div>

      <form
        className='tw-flex-1 tw-flex tw-flex-col'
        onSubmit={e => {
          e.preventDefault();
          void form.handleSubmit();
        }}
      >
        {/* Name Field */}
        <form.AppField
          name='name'
          validators={{
            onBlur: ({ value }) => {
              const result = safeParse(nameSchema, value);
              return result.success ? undefined : result.issues[0]?.message;
            },
          }}
        >
          {field => (
            <field.Input name='name' label={t("name_label")} placeholder={t("name_placeholder")} required={true} />
          )}
        </form.AppField>

        {/* Phone Number Field */}
        <form.AppField
          name='phone_number'
          validators={{
            onBlur: ({ value }) => {
              const result = safeParse(phoneSchema, value);
              return result.success ? undefined : result.issues[0]?.message;
            },
          }}
        >
          {field => (
            <field.PhoneInput
              name='phone_number'
              label={t("phone_label")}
              placeholder={t("phone_placeholder")}
              required={true}
              compactOnMobile={true}
            />
          )}
        </form.AppField>

        {/* Consent Checkbox */}
        <form.Field name='consent'>
          {field => (
            <label className='tw-flex tw-items-start tw-gap-3 tw-mb-8 tw-mt-4 tw-cursor-pointer'>
              <Checkbox size='lg' checked={field.state.value} onCheckedChange={value => field.handleChange(!!value)} />
              <span className='tw-type-2xs tw-text-gray-500'>
                {t.rich("consent_text", {
                  termsLink: chunks => (
                    <a
                      href='/terms-of-use'
                      target='_blank'
                      className='tw-text-default tw-underline tw-underline-offset-2 hover:tw-no-underline'
                    >
                      {chunks}
                    </a>
                  ),
                  privacyLink: chunks => (
                    <a
                      href='/privacy-policy'
                      target='_blank'
                      className='tw-text-default tw-underline tw-underline-offset-2 hover:tw-no-underline'
                    >
                      {chunks}
                    </a>
                  ),
                })}
              </span>
            </label>
          )}
        </form.Field>

        {/* Action Buttons */}

        <div className='tw-flex tw-items-center tw-justify-between tw-mt-auto tw-py-1.5 tw-gap-4'>
          <form.Subscribe selector={state => ({ canSubmit: state.canSubmit, isSubmitting: state.isSubmitting })}>
            {({ canSubmit, isSubmitting }) => (
              <div className='tw-flex tw-gap-3 tw-mt-auto tw-py-1.5'>
                <Button
                  type='button'
                  variant='elevated'
                  size='md'
                  onClick={onCancel}
                  disabled={isSubmitting}
                  icon={<ArrowLeft className='tw-size-4 sm:tw-hidden' />}
                >
                  <span className='tw-hidden sm:tw-inline'>{t("cancel")} </span>
                </Button>
                <form.Subscribe selector={state => state.values.consent}>
                  {consent => (
                    <Button
                      type='submit'
                      variant='primary'
                      size='md'
                      disabled={!canSubmit || isSubmitting || !consent}
                      icon={
                        isSubmitting ? (
                          <LoaderCircle className='tw-hidden tw-animate-spin sm:tw-block' size={14} />
                        ) : undefined
                      }
                    >
                      {t("get_call_button")}
                    </Button>
                  )}
                </form.Subscribe>
              </div>
            )}
          </form.Subscribe>
          <ResizableProgressBar currentStep={currentStep} totalSteps={totalSteps} />
        </div>
      </form>
    </HeroAgentCardLayout>
  );
}
